import {
    addToDateWithFormat,
    convertDateToUtc,
    getDateDifference,
    isDateBetween,
    subtractFromDateWithFormat
} from '../../utils/dates/date.utils';

import { INLINE_ELIGIBILITY_VALUES } from '../../constants/ssap/miscellaneous/miscellaneous';
import { PAGES } from '../../constants/ssap/flows/pagesTypes';
import { DATE_FORMAT2 } from '@utils/dates/dateMethods';
import { OTHER_PROGRAMS_TYPES } from '@constants/ssap/hhmTypes/otherProgramsList';

const {
    NO,
    NOT_APPLICABLE,
    YES
} = INLINE_ELIGIBILITY_VALUES;

const areChildrenHasHealthCoverageShown = params => {

    const {
        alwaysDisplayMedicaidQuestions,
        familyStatus,
        household,
        householdMembers,
        inlineCurrentIncomeEnabled,
        personId,
        preliminaryMedicaidStatus
    } = params;

    if(!familyStatus.relatedPersonsIds.children ||  household.primaryTaxFilerPersonId !== personId) {
        return {
            areChildrenShown: false,
            filteredChildren: []
        };
    }

    const filteredChildren = familyStatus.relatedPersonsIds.children.filter(ch => {
        const inlineEligibilityCriteria = [
            preliminaryMedicaidStatus
        ];

        if(
            alwaysDisplayMedicaidQuestions !== true &&
            inlineCurrentIncomeEnabled &&
            inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)
        ) {
            return !householdMembers[ch - 1].applyingForCoverageIndicator &&
                preliminaryMedicaidStatus === YES;
        } else {
            return !householdMembers[ch - 1].applyingForCoverageIndicator;
        }
    });

    return {
        areChildrenShown: filteredChildren.length > 0,
        filteredChildren
    };
};

const isMedicaidChipDenialShown = params => {

    const {
        hhm,
        medicaidTerminationFeatureEnabled,
        serverDate,
        medicaidEndDateOverride
    } = params;

    if(!medicaidTerminationFeatureEnabled) {
        return true;
    }

    const {
        medicaidTermination,
        medicaidChipExpirationDate,
        householdIncomeOrSizeChanged
    } = hhm;

    const futureThreshold = 60;
    const pastThreshold = medicaidEndDateOverride || 90;

    const isMedicaidChipExpirationDateWithin = isDateBetween(
        medicaidChipExpirationDate,
        subtractFromDateWithFormat(
            pastThreshold,
            'day',
            DATE_FORMAT2,
            convertDateToUtc(serverDate)
        ),
        addToDateWithFormat(
            futureThreshold,
            'day',
            DATE_FORMAT2,
            convertDateToUtc(serverDate)
        ),
        'days'
    );

    const conditions = [
        !medicaidTermination,
        medicaidTermination && !isMedicaidChipExpirationDateWithin,
        medicaidTermination && isMedicaidChipExpirationDateWithin && householdIncomeOrSizeChanged
    ];

    return conditions.some(condition => condition === true);
};

const isMemberWithAbsentParentShown = params => {

    const {
        alwaysDisplayMedicaidQuestions,
        applyingForCoverageIndicator,
        familyStatus,
        inlineCurrentIncomeEnabled,
        preliminaryEmergencyMedicaidStatus,
        preliminaryMedicaidStatus
    } = params;

    const isMemberTheChildWithoutParent = (()=> {
        const analyzeIfNotAllParents = value => !Array.isArray(value) ? true : value.length < 2;
        return applyingForCoverageIndicator && !familyStatus.currentHhm.isOlderThanChildAge && analyzeIfNotAllParents(familyStatus.relatedPersonsIds.parents);
    })();

    const inlineEligibilityCriteria = [
        preliminaryMedicaidStatus,
        preliminaryEmergencyMedicaidStatus
    ];

    if (
        alwaysDisplayMedicaidQuestions !== true &&
        inlineCurrentIncomeEnabled &&
        inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)
    ) {
        return isMemberTheChildWithoutParent && inlineEligibilityCriteria.some(v => v === YES);
    } else {
        return isMemberTheChildWithoutParent;
    }
};

const isMcoSelectionShown = params => {
    const {
        alwaysDisplayMedicaidQuestions,
        inlineCurrentIncomeEnabled,
        isState,
        preliminaryMedicaidStatus
    } = params;

    const inlineEligibilityCriteria = [
        preliminaryMedicaidStatus
    ];

    if(!isState.NM) {
        return false;
    }

    if (
        alwaysDisplayMedicaidQuestions !== true &&
        inlineCurrentIncomeEnabled &&
        inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)
    ) {
        return inlineEligibilityCriteria.some(v => v === YES);
    } else {
        return inlineEligibilityCriteria.some(v => v === YES);
    }
};

const isIndianHealthServicesShown = params => {

    const {
        alwaysDisplayMedicaidQuestions,
        applyingForCoverageIndicator,
        inlineCurrentIncomeEnabled,
        isItuQuestionsDisabled,
        memberOfFederallyRecognizedTribeIndicator,
        preliminaryChipStatus,
        preliminaryEmergencyMedicaidStatus,
        preliminaryMedicaidStatus,
    } = params;

    const inlineEligibilityCriteria = [
        preliminaryMedicaidStatus,
        preliminaryEmergencyMedicaidStatus,
        preliminaryChipStatus
    ];

    if (isItuQuestionsDisabled) {
        return false;
    }
    if (
        alwaysDisplayMedicaidQuestions !== true &&
        inlineCurrentIncomeEnabled &&
        inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)
    ) {
        return applyingForCoverageIndicator &&
            memberOfFederallyRecognizedTribeIndicator &&
            inlineEligibilityCriteria.some(v => v === YES);
    } else {
        return applyingForCoverageIndicator && memberOfFederallyRecognizedTribeIndicator;
    }
};

const isRetroactiveAssistanceShown = params => {

    const {
        alwaysDisplayMedicaidQuestions,
        applyingForCoverageIndicator,
        inlineCurrentIncomeEnabled,
        preliminaryEmergencyMedicaidStatus,
        preliminaryMedicaidStatus
    } = params;

    const inlineEligibilityCriteria = [
        preliminaryMedicaidStatus,
        preliminaryEmergencyMedicaidStatus
    ];

    if (
        alwaysDisplayMedicaidQuestions !== true &&
        inlineCurrentIncomeEnabled &&
        inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)
    ) {
        return applyingForCoverageIndicator && inlineEligibilityCriteria.some(v => v === YES);
    } else {
        return applyingForCoverageIndicator;
    }
};

const isSogiShown = params => {
    const {
        hhm,
        isSogiFeatureEnabled,
        serverDate
    } = params;

    const isHhmOlderThan12 = getDateDifference(serverDate, hhm?.dateOfBirth, 'years', true) >= 12;

    const conditions = [
        isHhmOlderThan12,
        isSogiFeatureEnabled
    ];

    return conditions.every(condition => condition === true);
};

const isEmployerCoverageShown = (params) => {
    const {
        currentOtherInsurance: {
            medicareEligibleIndicator,
            otherStateOrFederalPrograms,
            peaceCorpsIndicator,
            tricareEligibleIndicator
        } = {},
        hhm = {},
        hhmStatus,
        inlineCurrentIncomeEnabled,
        inlineEligibilityIgnoredForEscPage,
        preliminaryAptcCsrStatus,
        serverDate,
    } = params;

    const { COBRA_COVERAGE, RETIRE_HEALTH_BENEFITS, VETERANS_AFFAIRS_HCP } = OTHER_PROGRAMS_TYPES;
    const isOtherInsuranceEligible = otherStateOrFederalPrograms.some(({ eligible, type }) =>
        eligible && [COBRA_COVERAGE, RETIRE_HEALTH_BENEFITS, VETERANS_AFFAIRS_HCP].includes(type)
    );

    const shouldRender = [
        preliminaryAptcCsrStatus === YES,
        peaceCorpsIndicator === false,
        tricareEligibleIndicator === false,
        medicareEligibleIndicator === false,
        !isOtherInsuranceEligible
    ].every(condition => condition);

    // Check depending on inlineEligibilityIgnoredForEscPage setting is temporarily added
    // until the decision by Business is made: if we keep or remove updated visibility conditions
    const isBasicEligible = () => {
        const age = getDateDifference(serverDate, hhm.dateOfBirth, 'year');
        if (!hhm.applyingForCoverageIndicator) return false;
        if (age >= 14) return true;
        return hhmStatus.currentHhm.hasParentOrCaretakersWithEsc === true;
    };

    const result = (!inlineEligibilityIgnoredForEscPage && inlineCurrentIncomeEnabled && preliminaryAptcCsrStatus !== NOT_APPLICABLE)
        ? isBasicEligible() && shouldRender
        : isBasicEligible();

    return result;
};

export const questionsVisibility = {
    areChildrenHasHealthCoverageShown,
    isIndianHealthServicesShown,
    isMedicaidChipDenialShown,
    isMemberWithAbsentParentShown,
    isMcoSelectionShown,
    isRetroactiveAssistanceShown,
    isSogiShown,
    isEmployerCoverageShown
};

const additionalInformationPage = params => {
    const {
        familyStatusForCurrentHhm,
        primaryTaxFilerPersonId
    } = params;

    const { areChildrenShown } = areChildrenHasHealthCoverageShown({
        ...params,
        household: { primaryTaxFilerPersonId },
        familyStatus: familyStatusForCurrentHhm
    });

    const payload = [
        isIndianHealthServicesShown(params),
        isRetroactiveAssistanceShown(params),
        isMemberWithAbsentParentShown({ ...params, familyStatus: familyStatusForCurrentHhm }),
        areChildrenShown,
        isMcoSelectionShown(params),
    ];

    return [
        !payload.some(condition => condition),
        [
            PAGES.ADDITIONAL_INFORMATION_PAGE
        ],
        'person does not qualify for Indian Health Services, Retroactive Assistance, Absent Parent or Children with Health Coverage, MCO selection',
        // Next element in array is only for test purposes
        payload
    ];
};

const medicaidChipDenialInformationPage = params => {
    const {
        householdMembers,
        medicaidTerminationFeatureEnabled,
    } = params;
    if(!medicaidTerminationFeatureEnabled) {
        return [
            false,
            [
                PAGES.MEDICAID_CHIP_DENIAL_INFORMATION_PAGE
            ],
            'Medicaid termination feature is disabled'
        ];
    }

    const atLeastOneMemberHasMedicaidTermination = householdMembers.some(hhm => isMedicaidChipDenialShown({ ...params, hhm }));

    return [
        !atLeastOneMemberHasMedicaidTermination,
        [
            PAGES.MEDICAID_CHIP_DENIAL_INFORMATION_PAGE
        ],
        !atLeastOneMemberHasMedicaidTermination
            ? 'no member has Medicaid termination within the specified thresholds'
            : 'someone is qualified'
    ];
};

const stateEmployeeHealthBenefitPage = params => {

    const {
        alwaysDisplayMedicaidQuestions,
        applyingForCoverageIndicator,
        inlineCurrentIncomeEnabled,
        isState,
        preliminaryChipStatus,
        preliminaryEmergencyMedicaidStatus,
        preliminaryMedicaidStatus
    } = params;

    if(isState.NM) {
        return [
            true,
            [
                PAGES.STATE_EMPLOYEE_HEALTH_BENEFIT_PAGE
            ],
            'New Mexico state wants this page to be hidden'
        ];
    }

    const shouldNotRender = [
        preliminaryMedicaidStatus === NO,
        preliminaryChipStatus === NO,
        preliminaryEmergencyMedicaidStatus === NO
    ].every(condition => condition);

    const payload = (
        alwaysDisplayMedicaidQuestions !== true &&
        inlineCurrentIncomeEnabled &&
        preliminaryMedicaidStatus !== NOT_APPLICABLE &&
        preliminaryChipStatus !== NOT_APPLICABLE &&
        preliminaryEmergencyMedicaidStatus !== NOT_APPLICABLE
    )
        ? shouldNotRender
        : !applyingForCoverageIndicator;

    const explanation = inlineCurrentIncomeEnabled
        ? `all three values 
                         "preliminaryMedicaidStatus",
                         "preliminaryChipStatus" and 
                         "preliminaryEmergencyMedicaidStatus" 
                         are equal to NO`
        : '- person does not apply for coverage';

    return [
        payload,
        [
            PAGES.STATE_EMPLOYEE_HEALTH_BENEFIT_PAGE
        ],
        explanation
    ];
};

const checkEmployerCoverageDetailsPage = params => {
    const {
        inlineCurrentIncomeEnabled,
        inlineEligibilityIgnoredForEscPage,
    } = params;

    return [
        !isEmployerCoverageShown(params),
        [
            PAGES.EMPLOYER_COVERAGE_DETAIL_PAGE
        ],
        inlineEligibilityIgnoredForEscPage || !inlineCurrentIncomeEnabled
            ? '- person does not match basic eligibility'
            : `person does not match basic eligibility OR
                        "preliminaryAptcCsrStatus" !== YES OR 
                        "peaceCorpsIndicator" !== false OR 
                        "tricareEligibleIndicator" !== false OR 
                        "medicareEligibleIndicator" !== false OR 
                        otherStateOrFederalPrograms contains eligible any of these programs COBRA_COVERAGE / RETIRE_HEALTH_BENEFITS / VETERANS_AFFAIRS_HCP`

    ];
};

export const pageVisibility = {
    additionalInformationPage,
    medicaidChipDenialInformationPage,
    stateEmployeeHealthBenefitPage,
    checkEmployerCoverageDetailsPage
};